<template>
  <span v-if="props.anchor" :id="props.anchor" class="anchor"></span>
  <div class="card" v-bind="$attrs">
    <div class="card-header d-flex justify-content-between bg-primary">
      <h4 class="text-uppercase mb-0">Documents demand&eacute;s</h4>
      <button class="btn btn-link link-dark p-0 link-underline-opacity-0 link-underline-opacity-100-hover" @click="addDocument()"><i class="fa-solid fa-plus pe-1"></i>Ajouter un document</button>
    </div>
    <div class="card-body">
      <div v-if="documents.length === 0" class="text-muted">
        Aucun document demand&eacute;
      </div>
      <div v-else v-for="(doc,idx) in documents" :key="doc.key" class="card mb-3">
        <span :id="`documents[${idx}]`" class="anchor"></span>
        <div class="card-header d-flex justify-content-between bg-primary bg-opacity-25">
          <h4 class="text-uppercase mb-0">{{doc.value?.label.length > 0 ? doc.value?.label : `Document ${idx + 1}`}}</h4>
          <button class="btn btn-link link-dark p-0" @click="removeDocument(idx)"><i class="fa-regular fa-trash-can fa-lg"></i></button>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-12 col-md-6">
              <TextInputComponent
                :value-key="`documents[${idx}].label`"
                label="Nom"
                placeholder="Nom du document"
                :required="true"
                class="mb-3"
              />
            </div>
            <div class="col-12 col-md-6">
              <SelectInputComponent
                :value-key="`documents[${idx}].type`"
                label="Type"
                placeholder="Selectionnez un type"
                :options="Object.values(TravelDbDocumentTypeEnum).map(type => ({ value: type, label: GetDocumentTypeLabel(type) })).sort((a, b) => a.label.localeCompare(b.label))"
                :required="true"
                class="mb-3"
              />
            </div>
            <div class="col-12">
              <TextInputComponent
                :value-key="`documents[${idx}].subTitle`"
                label="Sous-Titre"
                placeholder="Sous-Titre du document"
                :required="false"
                class="mb-3"
              />
            </div>
            <div class="col-12 col-md-6">
              <CheckboxComponent
                :value-key="`documents[${idx}].required`"
                label="Requis ?"
                class="mb-3"
              />
            </div>
            <div class="col-12 col-md-6">
              <CheckboxComponent
                :value-key="`documents[${idx}].alsoForCompanion`"
                label="Concerne aussi les accompagnants ?"
                class="mb-3"
              />
            </div>
            <div class="col-12">
              <RichTextComponent
                :value-key="`documents[${idx}].instructions`"
                label="Instructions"
                placeholder="Instructions pour ce document (ex: format accepté, aide au remplissage etc.)"
                :required="false"
                type="multiline"
                class="mb-3"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useFieldArray } from 'vee-validate';
import TextInputComponent from '@/components/common/TextInputComponent.vue';
import SelectInputComponent from '@/components/common/SelectInputComponent.vue';
import { GetDocumentTypeLabel, IdNotDefined, ScrollToId } from '@/compositions/helper';
import * as zod from 'zod';

const validationSchema = defineModel<{ shape: zod.ZodRawShape, anchors?: Record<string, string | undefined> }>('validation-schema',{ required: false, default: () => ({ shape: {} }) });

const props = defineProps<{
  anchor?: string
}>();

const { remove: removeDocument, push: pushDocument, fields: documents } = useFieldArray<TravelDbDocument>(() => 'documents');

const defaultDocument = <TravelDbDocument>{
  id: IdNotDefined,
  type: TravelDbDocumentTypeEnum.Any,
  label: '',
  required: false,
  alsoForCompanion: false
}

const newDocument = ref<TravelDbDocument>(Object.assign({}, defaultDocument));

const addDocument = () => {
  pushDocument(newDocument.value);
  newDocument.value = Object.assign({}, defaultDocument);
  ScrollToId(`documents[${(documents?.value.length ?? 1) - 1}]`);
}

onMounted(() => {
  validationSchema.value = {
    shape:{
      documents: zod.array(zod.object({
        id: zod.number().nonnegative(),
        type: zod.nativeEnum(TravelDbDocumentTypeEnum, { message: 'Le type de document est requis' }),
        label: zod.string().min(1, 'Le nom du document est obligatoire'),
        subTitle: zod.string().nullish(),
        required: zod.boolean(),
        alsoForCompanion: zod.boolean().optional(),
        instructions: zod.string().nullish()
      })
    ).optional()
    },
    anchors: {
      "documents": props.anchor,
      "documents[\\d].id": "self",
      "documents[\\d].type": "self",
      "documents[\\d].label": "self",
      "documents[\\d].required": "self",
      "documents[\\d].alsoForCompanion": "self",
      "documents[\\d].instructions": "self",
      "documents[\\d].subTitle": "self"
    }
  }
});
</script>
